/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 */

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { EllipsisHorizontalIcon, GlobeAltIcon, ServerIcon, XCircleIcon, ClipboardIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline'
import { apiJson } from '../../lib/api'
import { usePageTitle } from '../../components/PageHeader'
import { formatDateTime } from '../../lib/utils'
import { ConfirmModal } from '../../components/Modal'

export type AdminTunnelRow = {
  id: string
  type: 'HTTP' | 'TCP'
  localAddress: string
  publicAddress: string
  lastActivity: string | null
  userName: string
  userId: string
  accountId: string
}

export default function AdminTunnels() {
  usePageTitle('Admin • Active Tunnels')

  const [rows, setRows] = useState<AdminTunnelRow[] | null>(null)
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)
  const [search, setSearch] = useState<string>('')
  const [confirmClose, setConfirmClose] = useState<AdminTunnelRow | null>(null)

  const refresh = (s?: string) => {
    const qs = s && s.trim().length > 0 ? `?search=${encodeURIComponent(s.trim())}` : ''
    void apiJson<AdminTunnelRow[]>(`/api/admin/tunnels${qs}`)
      .then(setRows)
      .catch(() => setRows([]))
  }

  useEffect(() => {
    refresh()
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-menu-root]')) setOpenMenuId(null)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [])

  useEffect(() => {
    const h = setTimeout(() => refresh(search), 300)
    return () => clearTimeout(h)
  }, [search])

  const onCloseTunnel = async (row: AdminTunnelRow) => {
    try {
      await apiJson<void>(`/api/admin/tunnels/${row.id}/close`, { method: 'POST' })
      refresh()
    } finally {
      setOpenMenuId(null)
      setConfirmClose(null)
    }
  }

  const copyToClipboard = (text: string) => {
    void navigator.clipboard.writeText(text)
    setOpenMenuId(null)
  }

  const data = useMemo(() => rows ?? [], [rows])

  return (
    <div className="flex flex-col max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-white">Active Tunnels</h2>
        <Link to="/app/admin" className="text-sm text-slate-400 hover:text-white">Back to Admin</Link>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-xl">
        <div className="px-6 py-4 border-b border-slate-800 bg-slate-800/50 flex items-center justify-between gap-4">
          <div className="font-semibold text-white">Active Tunnels ({data.length})</div>
          <div className="ml-auto">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by user or public address..."
              className="w-72 rounded-lg bg-slate-800 border border-slate-700 px-3 py-2 text-sm text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-slate-800/50">
              <tr>
                <th className="px-6 py-3 font-semibold text-slate-300">Type</th>
                <th className="px-6 py-3 font-semibold text-slate-300">Local Address</th>
                <th className="px-6 py-3 font-semibold text-slate-300">Public Address</th>
                <th className="px-6 py-3 font-semibold text-slate-300">Last Activity</th>
                <th className="px-6 py-3 font-semibold text-slate-300">User Name</th>
                <th className="px-6 py-3 text-right font-semibold text-slate-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-6 py-10 text-center text-slate-500">No active tunnels found</td>
                </tr>
              )}
              {data.map((r) => {
                const isHttp = r.type === 'HTTP'
                const publicUrl = isHttp && r.publicAddress.startsWith('http') ? r.publicAddress : null

                return (
                  <tr key={r.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                    <td className="px-6 py-3">
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${
                        isHttp 
                          ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20' 
                          : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20'
                      }`}>
                        {isHttp ? <GlobeAltIcon className="w-3 h-3" /> : <ServerIcon className="w-3 h-3" />}
                        {r.type}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-slate-300 font-mono text-xs">{r.localAddress}</td>
                    <td className="px-6 py-3">
                      {publicUrl ? (
                        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-indigo-400 hover:text-indigo-300 hover:underline font-mono text-xs break-all group">
                          {r.publicAddress}
                          <ArrowTopRightOnSquareIcon className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ) : (
                        <span className="text-slate-400 font-mono text-xs break-all">{r.publicAddress}</span>
                      )}
                    </td>
                    <td className="px-6 py-3 text-slate-400 text-xs">
                      {r.lastActivity ? formatDateTime(r.lastActivity) : '-'}
                    </td>
                    <td className="px-6 py-3 text-white">{r.userName || 'Unknown'}</td>
                    <td className="px-6 py-3 text-right">
                      <div className="relative inline-block text-left" data-menu-root>
                        <button
                          className="inline-flex items-center justify-center w-8 h-8 rounded-lg border border-slate-700 text-slate-400 hover:text-white hover:bg-slate-800"
                          onClick={(e) => { e.stopPropagation(); setOpenMenuId((v) => v === r.id ? null : r.id) }}
                          aria-label="Actions"
                        >
                          <EllipsisHorizontalIcon className="w-5 h-5" />
                        </button>
                        {openMenuId === r.id && (
                          <div className="absolute right-0 mt-2 w-56 rounded-xl border border-slate-800 bg-slate-900 shadow-2xl p-1 z-20">
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-slate-300 hover:text-white hover:bg-slate-800"
                              onClick={() => copyToClipboard(r.userId)}
                            >
                              <ClipboardIcon className="w-4 h-4"/> Copy User ID
                            </button>
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-slate-300 hover:text-white hover:bg-slate-800"
                              onClick={() => copyToClipboard(r.accountId)}
                            >
                              <ClipboardIcon className="w-4 h-4"/> Copy Account ID
                            </button>
                            <div className="my-1 border-t border-slate-800" />
                            <button
                              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left text-red-400 hover:text-red-300 hover:bg-slate-800"
                              onClick={() => setConfirmClose(r)}
                            >
                              <XCircleIcon className="w-4 h-4"/> Close tunnel
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {confirmClose && (
        <ConfirmModal
          isOpen={!!confirmClose}
          onClose={() => setConfirmClose(null)}
          onConfirm={() => onCloseTunnel(confirmClose)}
          title="Close tunnel?"
          message={`Are you sure you want to close tunnel from \"${confirmClose.userName}\" exposing \"${confirmClose.localAddress}\"?`}
          confirmText="Close tunnel"
          isDangerous
        />
      )}
    </div>
  )
}
